import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isPushSupported,
  getPushStatus,
  subscribeToPush,
} from "@/lib/pushNotifications";
import { toast } from "sonner";

/**
 * Iter178 — Slim top-app banner that prompts a rep to enable browser
 * push notifications when they're NOT yet subscribed. Appears on every
 * page (via AppLayout) so KOI alerts, streak-break nudges and rank-up
 * celebrations actually reach the rep instead of dying at `sent=0`.
 *
 * Visibility rules (all must be true):
 *   • Browser supports push (`isPushSupported()`)
 *   • Status is NOT subscribed
 *   • Notification permission is NOT 'denied' (denying kills the browser
 *     prompt — no point pestering; the Settings page handles the manual
 *     re-enable path)
 *   • User hasn't dismissed the banner in the last 14 days
 *     (localStorage flag `pushBannerDismissedAt`)
 *
 * A single "Turn on" button walks through Notification.requestPermission()
 * → PushManager.subscribe() → backend save via `subscribeToPush()`. Silent
 * error handling — we show a toast on success/failure.
 */
const DISMISS_KEY = "pushBannerDismissedAt";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isDismissedRecently() {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch { return false; }
}

function stampDismissed() {
  try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); }
  catch { /* ignore */ }
}

export default function PushEnableBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => isDismissedRecently());

  useEffect(() => {
    if (dismissed) return;
    if (!isPushSupported()) return;
    let cancelled = false;
    (async () => {
      const s = await getPushStatus();
      if (!cancelled) setStatus(s);
    })();
    return () => { cancelled = true; };
  }, [dismissed]);

  const handleEnable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) {
      toast.success("Push notifications enabled — you'll hear from us");
      setStatus((s) => ({ ...(s || {}), subscribed: true, permission: "granted" }));
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked. Enable them from your browser's site settings.");
      stampDismissed();
      setDismissed(true);
    } else if (res.reason === "default") {
      // User dismissed the browser prompt itself — quietly stamp so we
      // don't nag them again for 14 days.
      stampDismissed();
      setDismissed(true);
    } else {
      toast.error("Couldn't enable push. Try again from Settings.");
    }
  };

  const handleDismiss = () => {
    stampDismissed();
    setDismissed(true);
  };

  if (dismissed) return null;
  if (!status) return null;
  if (!status.supported) return null;
  if (status.subscribed) return null;
  if (status.permission === "denied") return null;

  return (
    <div
      className="bg-gradient-to-r from-emerald-900/60 via-emerald-800/40 to-emerald-900/60 border-b border-emerald-500/30 px-4 py-2 flex items-center gap-3 flex-wrap"
      data-testid="push-enable-banner"
    >
      <Bell className="w-4 h-4 text-emerald-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-emerald-100 font-medium">
          Turn on push notifications
        </div>
        <div className="text-[11px] text-emerald-200/70">
          Get instant KOI pace nudges, rank-up celebrations, and EOD reminders on this device.
        </div>
      </div>
      <Button
        size="sm"
        onClick={handleEnable}
        disabled={busy}
        data-testid="push-enable-banner-enable"
        className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-8"
      >
        {busy ? "Enabling…" : "Turn on"}
      </Button>
      <button
        onClick={handleDismiss}
        data-testid="push-enable-banner-dismiss"
        className="p-1 rounded hover:bg-white/10 text-emerald-200/70 hover:text-white transition-colors"
        title="Dismiss for 14 days"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
